import React, { useEffect, useRef, useState } from "react";
import { CTA_OPTIONS, BUSINESS_TYPES, COLOR_SCHEMES } from "../utils/prompts";

const INITIAL_STATE = {
  brandName: "",
  businessType: "",
  offer: "",
  targetAudience: "",
  headline: "",
  colorScheme: "",
  ctaButton: "",
  websiteUrl: "",
};

const MAX_REFERENCE_IMAGES = 3;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPT_IMAGES = "image/jpeg,image/png,image/webp,image/gif";

function CreationForm({ onSubmit, isLoading }) {
  const [form, setForm] = useState(INITIAL_STATE);
  const [referenceImages, setReferenceImages] = useState([]);
  const [expandedReference, setExpandedReference] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const required = ["brandName", "businessType", "offer", "targetAudience", "headline", "colorScheme", "ctaButton"];
  const hasWebsite = Boolean(form.websiteUrl?.trim());
  const hasRefImages = referenceImages.length > 0;

  useEffect(() => {
    if (!expandedReference) return undefined;
    function onKey(ev) {
      if (ev.key === "Escape") setExpandedReference(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandedReference]);

  function validate() {
    const errs = {};
    required.forEach((key) => {
      if (!form[key]?.trim()) errs[key] = "Обязательное поле";
    });
    if (hasWebsite && hasRefImages) {
      errs.mutual = "Укажите либо ссылку на сайт, либо загрузите примеры изображений — не оба варианта.";
    }
    if (form.websiteUrl?.trim() && !/^https?:\/\/.+/.test(form.websiteUrl.trim())) {
      errs.websiteUrl = "Введите корректный URL (https://...)";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function clearReferenceImages() {
    setReferenceImages([]);
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error("read"));
      reader.readAsDataURL(file);
    });
  }

  async function handleFileInputChange(e) {
    const input = e.target;
    const fileList = input?.files;
    if (!fileList?.length) return;
    const snapshot = Array.from(fileList);
    input.value = "";

    let nextErr = { ...errors, mutual: undefined, referenceImages: undefined };

    if (hasWebsite) {
      setForm((prev) => ({ ...prev, websiteUrl: "" }));
      nextErr.websiteUrl = undefined;
    }

    const remaining = MAX_REFERENCE_IMAGES - referenceImages.length;
    if (remaining <= 0) {
      setErrors((prev) => ({ ...prev, referenceImages: `Максимум ${MAX_REFERENCE_IMAGES} изображения` }));
      return;
    }

    const arr = snapshot.slice(0, remaining);
    const added = [];

    for (const file of arr) {
      if (!file.type.startsWith("image/")) {
        nextErr.referenceImages = "Допустимы только изображения (JPG, PNG, WebP, GIF)";
        break;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        nextErr.referenceImages = `Файл «${file.name}» слишком большой (макс. 5 МБ)`;
        break;
      }
      try {
        const dataUrl = await readFileAsDataUrl(file);
        added.push({ id: `${Date.now()}_${Math.random().toString(36).slice(2)}`, name: file.name, dataUrl });
      } catch {
        nextErr.referenceImages = "Не удалось прочитать файл";
        break;
      }
    }

    if (added.length) {
      setReferenceImages((prev) => [...prev, ...added].slice(0, MAX_REFERENCE_IMAGES));
    }
    setErrors(nextErr);
  }

  function removeReferenceImage(id) {
    setReferenceImages((prev) => prev.filter((x) => x.id !== id));
    setErrors((prev) => ({ ...prev, referenceImages: undefined, mutual: undefined }));
  }

  function handleChange(e) {
    const { name, value } = e.target;
    if (name === "websiteUrl" && value.trim()) {
      clearReferenceImages();
    }
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined, mutual: undefined }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      ...form,
      referenceImageDataUrls: referenceImages.map((r) => r.dataUrl),
    };
    onSubmit(payload);
  }

  return (
    <form className="aic-form" onSubmit={handleSubmit} noValidate>
      <div className="aic-form__header">
        <h3 className="aic-form__title">Настройки баннера</h3>
        <p className="aic-form__subtitle">Заполните форму — ИИ создаст 3 варианта дизайна</p>
      </div>

      <div className="aic-form__body">
        <div className={`aic-field ${errors.brandName ? "aic-field--error" : ""}`}>
          <label className="aic-field__label">
            Название бренда / компании <span className="aic-field__req">*</span>
          </label>
          <input
            className="aic-field__input"
            type="text"
            name="brandName"
            value={form.brandName}
            onChange={handleChange}
            placeholder="Например: OZON, Сбербанк, AutoMax..."
            disabled={isLoading}
          />
          {errors.brandName && <span className="aic-field__error">{errors.brandName}</span>}
        </div>

        <div className={`aic-field ${errors.businessType ? "aic-field--error" : ""}`}>
          <label className="aic-field__label">
            Сфера бизнеса <span className="aic-field__req">*</span>
          </label>
          <select
            className="aic-field__select"
            name="businessType"
            value={form.businessType}
            onChange={handleChange}
            disabled={isLoading}
          >
            <option value="">Выберите сферу...</option>
            {BUSINESS_TYPES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {errors.businessType && <span className="aic-field__error">{errors.businessType}</span>}
        </div>

        <div className={`aic-field ${errors.headline ? "aic-field--error" : ""}`}>
          <label className="aic-field__label">
            Заголовок баннера <span className="aic-field__req">*</span>
          </label>
          <input
            className="aic-field__input"
            type="text"
            name="headline"
            value={form.headline}
            onChange={handleChange}
            placeholder="Например: Скидка 50% только сегодня!"
            disabled={isLoading}
          />
          {errors.headline && <span className="aic-field__error">{errors.headline}</span>}
        </div>

        <div className={`aic-field ${errors.offer ? "aic-field--error" : ""}`}>
          <label className="aic-field__label">
            Основное предложение / оффер <span className="aic-field__req">*</span>
          </label>
          <textarea
            className="aic-field__textarea"
            name="offer"
            value={form.offer}
            onChange={handleChange}
            placeholder="Что предлагаете? Какая акция или уникальное предложение?"
            rows={3}
            disabled={isLoading}
          />
          {errors.offer && <span className="aic-field__error">{errors.offer}</span>}
        </div>

        <div className={`aic-field ${errors.targetAudience ? "aic-field--error" : ""}`}>
          <label className="aic-field__label">
            Целевая аудитория <span className="aic-field__req">*</span>
          </label>
          <input
            className="aic-field__input"
            type="text"
            name="targetAudience"
            value={form.targetAudience}
            onChange={handleChange}
            placeholder="Например: Мужчины 25–45, интересуются авто"
            disabled={isLoading}
          />
          {errors.targetAudience && <span className="aic-field__error">{errors.targetAudience}</span>}
        </div>

        {(errors.mutual || errors.referenceImages) && (
          <div className="aic-form__banner-error">
            {errors.mutual || errors.referenceImages}
          </div>
        )}

        <div className={`aic-field aic-field--optional ${hasWebsite ? "aic-field--disabled" : ""}`}>
          <label className="aic-field__label">
            Примеры изображений для стиля
            <span className="aic-field__badge">До 3 файлов</span>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_IMAGES}
            multiple
            className="aic-upload__input"
            onChange={handleFileInputChange}
            disabled={isLoading || hasWebsite}
          />
          <div className={`aic-upload ${hasWebsite ? "aic-upload--blocked" : ""}`}>
            <button
              type="button"
              className="aic-upload__btn"
              disabled={isLoading || hasWebsite || referenceImages.length >= MAX_REFERENCE_IMAGES}
              onClick={() => fileInputRef.current?.click()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {referenceImages.length >= MAX_REFERENCE_IMAGES ? "Лимит 3 файла" : "Выбрать изображения"}
            </button>
            <span className="aic-upload__hint">
              JPG, PNG, WebP, GIF · до 5 МБ каждый
              {hasWebsite && " · недоступно, пока указана ссылка на сайт"}
            </span>
            {!hasWebsite && referenceImages.length === 0 && (
              <p className="aic-upload__empty-hint">Выбранные файлы появятся в списке ниже</p>
            )}
          </div>

          {referenceImages.length > 0 && (
            <div className="aic-upload__preview-block">
              <div className="aic-upload__preview-head">
                <span className="aic-upload__preview-title">Загружено</span>
                <span className="aic-upload__preview-count">{referenceImages.length} из {MAX_REFERENCE_IMAGES}</span>
              </div>
              <ul className="aic-upload__list aic-upload__list--grid">
                {referenceImages.map((item) => (
                  <li key={item.id} className="aic-upload__item">
                    <div
                      className="aic-upload__thumb-wrap aic-upload__thumb-wrap--zoom"
                      role="button"
                      tabIndex={0}
                      aria-label={`Открыть изображение «${item.name}»`}
                      onClick={() => !isLoading && setExpandedReference({ dataUrl: item.dataUrl, name: item.name })}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                          ev.preventDefault();
                          if (!isLoading) setExpandedReference({ dataUrl: item.dataUrl, name: item.name });
                        }
                      }}
                    >
                      <img src={item.dataUrl} alt="" className="aic-upload__thumb" />
                    </div>
                    <span className="aic-upload__name" title={item.name}>{item.name}</span>
                    <button
                      type="button"
                      className="aic-upload__remove"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        removeReferenceImage(item.id);
                      }}
                      disabled={isLoading}
                      aria-label="Удалить"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="aic-form__row">
          <div className={`aic-field ${errors.colorScheme ? "aic-field--error" : ""}`}>
            <label className="aic-field__label">
              Цветовая гамма <span className="aic-field__req">*</span>
            </label>
            <select
              className="aic-field__select"
              name="colorScheme"
              value={form.colorScheme}
              onChange={handleChange}
              disabled={isLoading}
            >
              <option value="">Выберите...</option>
              {COLOR_SCHEMES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {errors.colorScheme && <span className="aic-field__error">{errors.colorScheme}</span>}
          </div>

          <div className={`aic-field ${errors.ctaButton ? "aic-field--error" : ""}`}>
            <label className="aic-field__label">
              Кнопка призыва к действию <span className="aic-field__req">*</span>
            </label>
            <select
              className="aic-field__select"
              name="ctaButton"
              value={form.ctaButton}
              onChange={handleChange}
              disabled={isLoading}
            >
              <option value="">Выберите...</option>
              {CTA_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {errors.ctaButton && <span className="aic-field__error">{errors.ctaButton}</span>}
          </div>
        </div>

        <div className={`aic-field aic-field--optional ${hasRefImages ? "aic-field--disabled" : ""}`}>
          <label className="aic-field__label">
            Ссылка на ваш сайт
            <span className="aic-field__badge">Необязательно</span>
          </label>
          <input
            className="aic-field__input"
            type="url"
            name="websiteUrl"
            value={form.websiteUrl}
            onChange={handleChange}
            placeholder="https://yoursite.ru"
            disabled={isLoading || hasRefImages}
          />
          <span className="aic-field__hint">
            Мы возьмём фотографии и стиль с вашего сайта для генерации
            {hasRefImages && " · недоступно, пока загружены примеры изображений"}
          </span>
          {errors.websiteUrl && <span className="aic-field__error">{errors.websiteUrl}</span>}
        </div>
      </div>

      {expandedReference && (
        <div
          className="aic-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Просмотр изображения"
          onClick={() => setExpandedReference(null)}
        >
          <div className="aic-ref-lightbox" onClick={(ev) => ev.stopPropagation()}>
            <button
              type="button"
              className="aic-ref-lightbox__close"
              onClick={() => setExpandedReference(null)}
              aria-label="Закрыть"
            >
              ×
            </button>
            <img
              src={expandedReference.dataUrl}
              alt={expandedReference.name}
              className="aic-ref-lightbox__img"
            />
            <p className="aic-ref-lightbox__caption">{expandedReference.name}</p>
          </div>
        </div>
      )}

      <div className="aic-form__footer">
        <button
          type="submit"
          className="aic-btn aic-btn--generate"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="aic-btn__spinner" />
              Генерируем...
            </>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
              </svg>
              Сгенерировать за 500 ₽
            </>
          )}
        </button>
        {/* <p className="aic-form__price-note">
          Списание только после подтверждения результата
        </p> */}
      </div>
    </form>
  );
}

export default CreationForm;
